import { registerPlugin, WebPlugin } from '@capacitor/core';

// Define permission result shape
export type HealthKitPermissionStatus =
    | 'authorized'
    | 'denied'
    | 'notAvailable';

export type HealthProviderName = 'HEALTH_CONNECT' | 'HUAWEI_HEALTH_KIT' | 'SAMSUNG_HEALTH';

export interface HealthProviderStatusResult {
    provider: HealthProviderName | null;
    choiceRequired: boolean;
    options: HealthProviderName[];
    healthConnectAvailable: boolean;
    huaweiHealthAvailable: boolean;
    samsungHealthInstalled: boolean;
}

// Plugin interface
export interface VivaScoreConnectPlugin {
    configure(options: {
        endpoint: string;
        token: string;
        privacyPolicyURL: string;
        enableSamsungHealth?: boolean;
        enableHuaweiHealth?: boolean;
    }): Promise<void>;

    disconnect(): Promise<void>;

    /** iOS: BGAppRefresh submit. Android: WorkManager periodic sync. Best-effort only. */
    scheduleBackgroundAppRefreshRequest(): Promise<void>;

    /** Native health sync now (iOS: HK + upload; Android: WorkManager one-off). */
    requestImmediateHealthDataSync(): Promise<void>;

    /**
     * Fire-and-forget full sync (all monitors). Resolves immediately; sync runs in the background.
     * Android: WorkManager REPLACE. iOS: detached Task.
     */
    requestFullSync(): Promise<void>;

    /**
     * Android: enable/disable the ongoing background-sync status notification.
     * Does not stop WorkManager scheduling. iOS: no-op.
     */
    setBackgroundSyncNotificationEnabled(options: { enabled: boolean }): Promise<void>;

    /**
     * Android: whether the status notification may be shown (default true).
     * iOS: always { enabled: true }.
     */
    isBackgroundSyncNotificationEnabled(): Promise<{ enabled: boolean }>;

    getConnected(): Promise<{ connected: boolean }>;

    requestHealthKitPermissions(): Promise<{ status: HealthKitPermissionStatus; grantedTypes?: string[]; }>;

    requestHealthConnectPermissions(): Promise<{ status: string; success: boolean }>;
    isHealthConnectAvailable(): Promise<{ available: boolean }>;
    getHealthProviderStatus(): Promise<HealthProviderStatusResult>;
    setHealthProvider(options: { provider: HealthProviderName }): Promise<void>;
    selectHealthProviderAndRequestPermissions(
        options: { provider: HealthProviderName }
    ): Promise<{ status: string; success: boolean }>;
    requestActiveProviderPermissions(): Promise<{ status: string; success: boolean }>;
    
    /**
     * Get the timestamp of the last successfully sent record to the server.
     * Returns null if no records have been sent yet.
     */
    getLastRecordSent(): Promise<{ timestamp: string | null }>;
    
    /**
     * Get the last known location data from IPInfo API.
     * Returns complete LocationModel with coordinates, city, region, country, postal, timezone, and API metadata.
     * Returns null values if no location data is available.
     */
    getLastLocation(): Promise<{ 
        latitude: number | null; 
        longitude: number | null; 
        timestamp: string | null;
        city: string | null;
        region: string | null;
        country: string | null;
        postal: string | null;
        timezone: string | null;
        lastError: string | null;
        lastIPInfoBody: string | null;
        lastIPInfoFetch: string | null;
    }>;
    
    /**
     * Get all stored physical summaries from cache.
     */
    getPhysicalSummaries(): Promise<{ data: string | null }>;
    
    /**
     * Get all stored sleep summaries from cache.
     */
    getSleepSummaries(): Promise<{ data: string | null }>;
    
    /**
     * Get all stored workout events from cache.
     */
    getWorkoutEvents(): Promise<{ data: string | null }>;
    
    /**
     * Get all stored blood pressure events from cache.
     */
    getBloodPressureEvents(): Promise<{ data: string | null }>;
    
    /**
     * Get all stored weight events from cache.
     */
    getWeightEvents(): Promise<{ data: string | null }>;
    
    /**
     * Get all stored body fat events from cache.
     */
    getBodyFatEvents(): Promise<{ data: string | null }>;
}

// Web fallback implementation
export class VivaScoreConnectPluginWeb extends WebPlugin implements VivaScoreConnectPlugin {
    private notAvailable(): never {
        throw new Error('VivaScoreConnectPlugin is not available on web.');
    }

    async configure(): Promise<void> {
        this.notAvailable();
    }

    async disconnect(): Promise<void> {
        this.notAvailable();
    }

    async scheduleBackgroundAppRefreshRequest(): Promise<void> {
        this.notAvailable();
    }

    async requestImmediateHealthDataSync(): Promise<void> {
        this.notAvailable();
    }

    async requestFullSync(): Promise<void> {
        this.notAvailable();
    }

    async setBackgroundSyncNotificationEnabled(): Promise<void> {
        this.notAvailable();
    }

    async isBackgroundSyncNotificationEnabled(): Promise<{ enabled: boolean }> {
        this.notAvailable();
    }

    async getConnected(): Promise<{ connected: boolean }> {
        this.notAvailable();
    }

    async requestHealthKitPermissions(): Promise<{
        status: HealthKitPermissionStatus;
        grantedTypes?: string[];
    }> {
        this.notAvailable();
    }

    async requestHealthConnectPermissions(): Promise<{ status: string; success: boolean }> {
        this.notAvailable();
    }

    async isHealthConnectAvailable(): Promise<{ available: boolean }> {
        this.notAvailable();
    }

    async getHealthProviderStatus(): Promise<HealthProviderStatusResult> {
        this.notAvailable();
    }

    async setHealthProvider(): Promise<void> {
        this.notAvailable();
    }

    async selectHealthProviderAndRequestPermissions(): Promise<{ status: string; success: boolean }> {
        this.notAvailable();
    }

    async requestActiveProviderPermissions(): Promise<{ status: string; success: boolean }> {
        this.notAvailable();
    }

    async getLastRecordSent(): Promise<{ timestamp: string | null }> {
        this.notAvailable();
    }

    async getLastLocation(): Promise<{ 
        latitude: number | null; 
        longitude: number | null; 
        timestamp: string | null;
        city: string | null;
        region: string | null;
        country: string | null;
        postal: string | null;
        timezone: string | null;
        lastError: string | null;
        lastIPInfoBody: string | null;
        lastIPInfoFetch: string | null;
    }> {
        this.notAvailable();
    }

    async getPhysicalSummaries(): Promise<{ data: string | null }> {
        this.notAvailable();
    }

    async getSleepSummaries(): Promise<{ data: string | null }> {
        this.notAvailable();
    }

    async getWorkoutEvents(): Promise<{ data: string | null }> {
        this.notAvailable();
    }

    async getBloodPressureEvents(): Promise<{ data: string | null }> {
        this.notAvailable();
    }

    async getWeightEvents(): Promise<{ data: string | null }> {
        this.notAvailable();
    }

    async getBodyFatEvents(): Promise<{ data: string | null }> {
        this.notAvailable();
    }
}

// Register the plugin
const VivaScoreConnect = registerPlugin<VivaScoreConnectPlugin>('VivaScoreConnectPlugin', {
    web: () => new VivaScoreConnectPluginWeb(),
});

export default VivaScoreConnect;